// Admin slot for the FOM Pre-Approval Letter shown from the Landing milestone banner.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileCheck2, Trash2, UploadCloud } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const errMsg = (e) => formatApiError(e.response?.data?.detail) || "Something went wrong";

export default function MilestoneLetterCard() {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/milestone/letter").then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Files must be 25 MB or smaller."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/admin/milestone/letter", fd);
      setMeta((m) => ({ ...m, ...r.data, original_filename: file.name }));
      toast.success("Letter posted. The banner button is live for signed-in members.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the letter? The banner button will be hidden.")) return;
    try { await api.delete("/admin/milestone/letter"); toast.success("Letter removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="border border-bdu-gold/25 bg-bdu-surface2 p-6 flex flex-wrap items-center justify-between gap-5" data-testid="admin-milestone-letter">
      <div className="flex items-start gap-4 min-w-0">
        <FileCheck2 size={22} className="text-bdu-gold shrink-0 mt-1" />
        <div className="min-w-0">
          <div className="label-eyebrow">Milestone banner · members only</div>
          <div className="mt-1 font-serif text-lg text-bdu-cream">FOM Pre-Approval Letter</div>
          <div className="mt-1 text-xs text-bdu-cream/55" data-testid="admin-milestone-letter-status">
            {!meta ? "Loading…" : meta.available
              ? `Posted · ${meta.original_filename || meta.filename} · ${new Date(meta.uploaded_at).toLocaleDateString()}`
              : "Not posted yet. The banner button stays hidden until you upload the letter."}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        {meta?.available && (
          <a href="/milestone-letter" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs" data-testid="admin-milestone-letter-view">View</a>
        )}
        <label className={`btn-gold text-xs inline-flex items-center gap-2 cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <UploadCloud size={14} /> {busy ? "Uploading…" : meta?.available ? "Replace letter" : "Upload letter"}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="admin-milestone-letter-input"
                 onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {meta?.available && (
          <button type="button" onClick={remove} className="text-xs uppercase tracking-[0.2em] border border-red-400/30 hover:bg-red-400/10 px-3 py-2 text-red-300 inline-flex items-center gap-1.5"
                  data-testid="admin-milestone-letter-remove">
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>
    </section>
  );
}
